import type { GetStaffMeResponse, StaffMemberListItem, StaffRoleListItem } from "@wallyo/contracts/console";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { listRoles, listStaff } from "../../api/console-api";
import { useApi } from "../../api/context";
import { useAlerts } from "../../shell/alerts";
import { useStaff } from "../../shell/staff-context";
import {
  DeactivateDialog,
  ReactivateDialog,
  EndSessionsDialog,
  FeatureDialog,
  InviteDialog,
  MembershipDialog,
  PermissionDialog,
  RoleDialog,
} from "./TeamDialogs";
import { upsert } from "./team-writes";
import { type TeamIntent } from "./TeamView";
import { RecordsView } from "../hr/RecordsView";
import { NO_RECORDS_FILTERS, type RecordsFilters } from "../hr/records-model";

type Loaded = { staff: StaffMemberListItem[]; roles: StaffRoleListItem[] };
type State =
  | { status: "loading"; data: Loaded | null }
  | { status: "ready"; data: Loaded }
  | { status: "failed"; data: Loaded | null; error: unknown };

const memberId = (member: StaffMemberListItem) => member.staffMemberId;
const roleId = (role: StaffRoleListItem) => role.staffRoleId;

/**
 * Records, the first tab under Employees (WALLIYO-0131): GET /console/staff
 * and /console/roles, and every write in a dialog that asks for a reason. The
 * screen changes only once the server has answered (never optimistic); after
 * each success both lists are read again so member counts and versions stay
 * true.
 *
 * No title and no tab strip of its own: it sits inside Human Resources, which
 * draws both. Two headers on one screen is two screens' worth of furniture and
 * one screen's worth of content.
 */
export function RecordsTab() {
  const { client } = useApi();
  const { me } = useStaff();
  const [state, setState] = useState<State>({ status: "loading", data: null });
  const [intent, setIntent] = useState<TeamIntent | null>(null);
  const [filters, setFilters] = useState<RecordsFilters>(NO_RECORDS_FILTERS);
  const alerts = useAlerts();
  const [reloads, reload] = useReducer((count: number) => count + 1, 0);
  const generation = useRef(0);

  useEffect(() => {
    const mine = ++generation.current;
    setState((current) => ({ status: "loading", data: current.data }));
    Promise.all([listStaff(client), listRoles(client)]).then(
      ([staff, roles]) => {
        if (generation.current !== mine) return;
        setState({ status: "ready", data: { staff: [...staff.staffMembers], roles: [...roles.roles] } });
      },
      (error: unknown) => {
        if (generation.current === mine) setState((current) => ({ status: "failed", data: current.data, error }));
      },
    );
  }, [client, reloads]);

  const patch = useCallback((change: (data: Loaded) => Loaded) => {
    setState((current) => (current.data ? ({ ...current, data: change(current.data) } as State) : current));
  }, []);
  const onMember = (member: StaffMemberListItem) =>
    patch((data) => ({ ...data, staff: upsert(data.staff, member, memberId) }));
  const onRole = (role: StaffRoleListItem) => patch((data) => ({ ...data, roles: upsert(data.roles, role, roleId) }));
  const close = useCallback(() => setIntent(null), []);
  const done = (message: string, keepOpen = false) => {
    alerts.ok(message);
    if (!keepOpen) setIntent(null);
    reload();
  };
  const reloadAfterStale = () => {
    setIntent(null);
    reload();
  };

  const data = state.data;
  const common = {
    onClose: close,
    onReload: reloadAfterStale,
    onDone: (message: string) => done(message),
  };

  return (
    <>
      <RecordsView
        state={
          data
            ? state.status === "failed"
              ? { status: "failed", staff: data.staff, error: state.error }
              : { status: state.status === "ready" ? "ready" : "loading", staff: data.staff }
            : state.status === "failed"
              ? { status: "failed", staff: null, error: state.error }
              : { status: "loading", staff: null }
        }
        filters={filters}
        onFilters={setFilters}
        onInvite={() => setIntent({ kind: "invite" })}
        onOpen={(member) => setIntent({ kind: "roles", member })}
        onRetry={reload}
      />
      {intent && data ? (
        <TeamDialog
          intent={intent}
          me={me}
          roles={data.roles}
          staff={data.staff}
          selfId={me.staffMember.staffMemberId}
          common={common}
          onMember={onMember}
          onRole={onRole}
          onMembershipDone={(message) => done(message, true)}
        />
      ) : null}
    </>
  );
}

function TeamDialog(props: {
  intent: TeamIntent;
  me: GetStaffMeResponse;
  roles: readonly StaffRoleListItem[];
  staff: readonly StaffMemberListItem[];
  selfId: string;
  common: { onClose: () => void; onReload: () => void; onDone: (message: string) => void };
  onMember: (member: StaffMemberListItem) => void;
  onRole: (role: StaffRoleListItem) => void;
  onMembershipDone: (message: string) => void;
}) {
  const { intent, common } = props;
  switch (intent.kind) {
    case "invite":
      return <InviteDialog {...common} roles={props.roles} onMember={props.onMember} />;
    case "deactivate":
      return <DeactivateDialog {...common} member={intent.member} onMember={props.onMember} />;
    case "reactivate":
      return <ReactivateDialog {...common} member={intent.member} onMember={props.onMember} />;
    case "endSessions":
      return (
        <EndSessionsDialog
          {...common}
          member={intent.member}
          self={intent.member.staffMemberId === props.selfId}
          onEnded={() => props.onMember({ ...intent.member, openSessions: 0 })}
        />
      );
    case "roles": {
      // The member as the list shows it now, so a change made in the dialog shows in it.
      const member = props.staff.find((each) => each.staffMemberId === intent.member.staffMemberId) ?? intent.member;
      return (
        <MembershipDialog
          {...common}
          onDone={props.onMembershipDone}
          member={member}
          roles={props.roles}
          onMember={props.onMember}
        />
      );
    }
    case "feature":
      return (
        <FeatureDialog
          {...common}
          role={intent.role}
          feature={intent.feature}
          enabled={intent.enabled}
          onRole={props.onRole}
        />
      );
    case "permission":
      return (
        <PermissionDialog
          {...common}
          me={props.me}
          role={intent.role}
          permission={intent.permission}
          onRole={props.onRole}
        />
      );
    case "createRole":
      return <RoleDialog {...common} role={null} onRole={props.onRole} />;
    case "editRole":
      return <RoleDialog {...common} role={intent.role} onRole={props.onRole} />;
  }
}
